import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutGrid, Users, Building2, Handshake, FolderKanban, CheckSquare,
  Settings, LogOut, Moon, Sun, Globe, Shield, CalendarDays, BarChart3, HelpCircle,
  MoreHorizontal, X, CalendarClock,
} from "lucide-react";
import { useAuth, can } from "../auth";
import i18n from "../i18n";
import api from "../api";
import NotificationBell from "./NotificationBell";
import { NotificationProvider } from "../context/NotificationContext";
import { useBranding } from "../context/BrandingContext";
import { BrandIcon, Wordmark } from "./Brand";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutGrid, exact: true },
  { to: "/contacts", key: "contacts", icon: Users },
  { to: "/companies", key: "companies", icon: Building2 },
  { to: "/deals", key: "deals", icon: Handshake },
  { to: "/projects", key: "projects", icon: FolderKanban },
  { to: "/activities", key: "activities", icon: CheckSquare },
  { to: "/calendar", key: "calendar", icon: CalendarDays },
];

const MOBILE_NAV = [
  { to: "/", key: "dashboard", icon: LayoutGrid, exact: true },
  { to: "/contacts", key: "contacts", icon: Users },
  { to: "/deals", key: "deals", icon: Handshake },
  { to: "/projects", key: "projects", icon: FolderKanban },
  { to: "/calendar", key: "calendar", icon: CalendarDays },
];

// Items that only show up in the mobile bottom nav's "More" sheet -- the bar
// itself only has room for a handful of tabs, so anything not in MOBILE_NAV
// lives here instead of being unreachable on mobile.
const MOBILE_MORE_BASE = [
  { to: "/companies", key: "companies", icon: Building2 },
  { to: "/activities", key: "activities", icon: CheckSquare },
];

function useTheme() {
  const [dark, setDark] = useState(
    () => (localStorage.getItem("theme") || "dark") === "dark"
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useTheme();
  const [lang, setLang] = useState(i18n.language);
  const [moreOpen, setMoreOpen] = useState(false);

  const toggleLang = async () => {
    const next = lang === "en" ? "hu" : "en";
    setLang(next);
    i18n.changeLanguage(next);
    localStorage.setItem("locale", next);
    try {
      await api.put("/users/me/locale", { locale: next });
    } catch (e) {}
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  // A configured tagline overrides the translated one in every language -- a
  // company strapline isn't ours to translate. Empty (the default) keeps the
  // shipped product's tagline in the viewer's own language.
  const tagline = branding.tagline || t("app.tagline");

  const nav = [...NAV];
  const mobileMore = [...MOBILE_MORE_BASE];
  // Meetings is hidden from guests: the backend blocks them from reading meeting
  // content at all (free-text summaries can't be financially masked), so a nav
  // link would only lead to a page that 403s.
  if (can.write(user)) {
    nav.push({ to: "/meetings", key: "meetings", icon: CalendarClock });
    mobileMore.push({ to: "/meetings", key: "meetings", icon: CalendarClock });
  }
  if (user && (user.role === "admin" || user.role === "manager")) {
    nav.push({ to: "/utilization", key: "utilization", icon: BarChart3 });
    nav.push({ to: "/deal-flow", key: "dealFlow", icon: Handshake });
    mobileMore.push({ to: "/utilization", key: "utilization", icon: BarChart3 });
    mobileMore.push({ to: "/deal-flow", key: "dealFlow", icon: Handshake });
  }
  if (can.admin(user)) {
    nav.push({ to: "/users", key: "users", icon: Shield });
    nav.push({ to: "/settings", key: "settings", icon: Settings });
    mobileMore.push({ to: "/users", key: "users", icon: Shield });
    mobileMore.push({ to: "/settings", key: "settings", icon: Settings });
  }
  const moreIsActive = mobileMore.some((item) => location.pathname.startsWith(item.to));

  const closeMore = () => setMoreOpen(false);
  const goTo = (to) => {
    setMoreOpen(false);
    navigate(to);
  };

  return (
    <NotificationProvider>
    <div className="min-h-screen bg-bg text-txt flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border shrink-0 sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2">
            <BrandIcon className="w-7 h-7" />
            <Wordmark className="font-display text-2xl font-black tracking-tighter" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted mt-1">
            {tagline}
          </div>
        </div>
        <nav data-testid="nav-sidebar" className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.exact}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-muted hover:bg-surface hover:text-txt"
                }`
              }
            >
              <item.icon size={18} />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-border space-y-1">
          <button
            onClick={() => navigate("/?tour=replay")}
            data-testid="replay-tour-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-muted hover:bg-surface hover:text-txt transition-colors"
          >
            <HelpCircle size={18} /> {t("onboarding.replayTour")}
          </button>
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-muted hover:bg-surface hover:text-txt transition-colors"
          >
            <Globe size={18} /> {lang === "en" ? "Magyar" : "English"}
          </button>
          <button
            onClick={() => setDark(!dark)}
            data-testid="theme-toggle"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-muted hover:bg-surface hover:text-txt transition-colors"
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
            {dark ? t("settings.light") : t("settings.dark")}
          </button>
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="w-8 h-8 rounded-sm bg-primary/20 text-primary flex items-center justify-center text-sm font-bold shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted">{t(`roles.${user?.role}`)}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="text-muted hover:text-danger transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-border bg-frost backdrop-blur-xl">
          <div className="flex items-center gap-2">
            <BrandIcon className="w-6 h-6" />
            <Wordmark className="font-display text-xl font-black tracking-tighter" />
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell testId="notif-bell-mobile" />
            <button onClick={() => navigate("/?tour=replay")} data-testid="replay-tour-btn-mobile" className="p-2 rounded-sm hover:bg-surface transition-colors text-muted">
              <HelpCircle size={18} />
            </button>
            <button onClick={toggleLang} data-testid="lang-toggle-mobile" className="p-2 rounded-sm hover:bg-surface transition-colors text-muted">
              <Globe size={18} />
            </button>
            <button onClick={() => setDark(!dark)} data-testid="theme-toggle-mobile" className="p-2 rounded-sm hover:bg-surface transition-colors text-muted">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={doLogout} data-testid="logout-btn-mobile" className="p-2 rounded-sm hover:bg-surface transition-colors text-muted">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-28 lg:pb-8 max-w-7xl w-full mx-auto">
          <div className="hidden lg:flex justify-end mb-2 -mt-2">
            <NotificationBell testId="notif-bell-desktop" />
          </div>
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav data-testid="nav-mobile-bar" className="lg:hidden fixed bottom-0 inset-x-0 z-40 mx-3 mb-3 rounded-sm border border-border bg-frost backdrop-blur-xl shadow-2xl">
        <div className="flex items-center justify-around">
          {MOBILE_NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.exact}
              data-testid={`nav-mobile-${item.key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 flex-1 text-[10px] transition-colors ${
                  isActive ? "text-primary" : "text-muted"
                }`
              }
            >
              <item.icon size={20} />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen((o) => !o)}
            data-testid="nav-mobile-more"
            aria-expanded={moreOpen}
            className={`flex flex-col items-center gap-1 py-2.5 flex-1 text-[10px] transition-colors ${
              moreOpen || moreIsActive ? "text-primary" : "text-muted"
            }`}
          >
            <MoreHorizontal size={20} />
            {t("nav.more")}
          </button>
        </div>
      </nav>

      {/* Mobile "More" sheet -- everything the bottom nav has no room for
          (Companies, Activities, and role-gated Reports/Users/Settings). */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" data-testid="nav-mobile-more-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={closeMore} />
          <div className="relative w-full mx-3 mb-20 rounded-sm border border-border bg-bg shadow-2xl overflow-hidden animate-fade-up">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="font-display font-bold text-sm">{t("nav.more")}</span>
              <button onClick={closeMore} data-testid="nav-mobile-more-close" className="text-muted hover:text-txt transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="py-1">
              {mobileMore.map((item) => {
                const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                return (
                  <button
                    key={item.key}
                    onClick={() => goTo(item.to)}
                    data-testid={`nav-mobile-${item.key}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${
                      active ? "text-primary" : "text-txt hover:bg-surface"
                    }`}
                  >
                    <item.icon size={18} />
                    {t(`nav.${item.key}`)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
    </NotificationProvider>
  );
}
